import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  FormControl,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  TextField,
  Avatar,
  IconButton,
  Stack,
  Chip,
  useTheme,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Palette as PaletteIcon,
  ZoomIn as ZoomInIcon,
  ViewCompact as ViewCompactIcon,
  Brightness4 as DarkIcon,
  Brightness7 as LightIcon,
  Key as KeyIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  TextFields as TextFieldsIcon,
  FormatSize as FormatSizeIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useColorMode } from '../../contexts/ColorModeContext';
import { useDisplaySize } from '../../contexts/DisplaySizeContext';
import { useAuth } from '../../contexts/AuthContext';
import { apiKeysService, ApiKeyResponse } from '../../services/apiKeys-service';

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const { size, setDisplaySize } = useDisplaySize();
  const { mode, toggleColorMode } = useColorMode();
  const { user } = useAuth();
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  
  // Profile form state
  const [profileData, setProfileData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    clientId: user?.clientId || 0,
    subdomain: user?.subdomain || '',
  });

  // API Key state
  const [apiKeyData, setApiKeyData] = useState<{
    apiKey: string;
    createdDate: string;
    lastUsedDate?: string;
    isActive: boolean;
  } | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [isLoadingApiKey, setIsLoadingApiKey] = useState(false);
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);
  const [isRevokingKey, setIsRevokingKey] = useState(false);
  const [apiKeyError, setApiKeyError] = useState<string | null>(null);
  const [apiKeySuccess, setApiKeySuccess] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleProfileChange = (field: string, value: string) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveProfile = () => {
    // TODO: Implement profile update logic
    console.log('Saving profile:', profileData);
  };

  // API Key management functions
  const fetchApiKey = async () => {
    setIsLoadingApiKey(true);
    setApiKeyError(null);
    try {
      const response = await apiKeysService.getMyApiKey();
      setApiKeyData(response.data);
    } catch (error) {
      setApiKeyError('Error fetching API key');
    } finally {
      setIsLoadingApiKey(false);
    }
  };

  const generateNewApiKey = async () => {
    setIsGeneratingKey(true);
    setApiKeyError(null);
    setApiKeySuccess(null);
    try {
      const response = await apiKeysService.generateNewApiKey();
      setApiKeyData({
        apiKey: response.data.apiKey,
        createdDate: new Date().toISOString(),
        isActive: true
      });
      setApiKeySuccess('New API key generated successfully');
      setShowApiKey(true);
    } catch (error) {
      setApiKeyError('Error generating API key');
    } finally {
      setIsGeneratingKey(false);
    }
  };

  const revokeApiKey = async () => {
    setIsRevokingKey(true);
    setApiKeyError(null);
    setApiKeySuccess(null);
    try {
      await apiKeysService.revokeApiKey('Revoked by user');
      setApiKeyData(null);
      setApiKeySuccess('API key revoked successfully');
      setShowApiKey(false);
    } catch (error) {
      setApiKeyError('Error revoking API key');
    } finally {
      setIsRevokingKey(false);
    }
  };

  const copyApiKey = async () => {
    if (!apiKeyData?.apiKey) return;
    
    try {
      await navigator.clipboard.writeText(apiKeyData.apiKey);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy API key:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = apiKeyData.apiKey;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  // Load API key when API Keys tab is selected
  React.useEffect(() => {
    if (activeTab === 2 && !apiKeyData && !isLoadingApiKey) {
      fetchApiKey();
    }
  }, [activeTab]); // Only depend on activeTab to prevent infinite loop

  const getDisplaySizeIcon = (sizeOption: 'extra small' | 'small' | 'medium' | 'large') => {
    switch (sizeOption) {
      case 'extra small':
        return <TextFieldsIcon fontSize="small" sx={{ fontSize: '0.75rem' }} />;
      case 'small':
        return <TextFieldsIcon fontSize="small" sx={{ fontSize: '0.875rem' }} />;
      case 'medium':
        return <TextFieldsIcon fontSize="small" sx={{ fontSize: '1rem' }} />;
      case 'large':
        return <TextFieldsIcon fontSize="small" sx={{ fontSize: '1.25rem' }} />;
      default:
        return <TextFieldsIcon fontSize="small" />;
    }
  };

  const getDisplaySizeLabel = (sizeOption: 'extra small' | 'small' | 'medium' | 'large') => {
    switch (sizeOption) {
      case 'extra small':
        return 'Extra Small';
      case 'small':
        return 'Small';
      case 'large':
        return 'Large';
      default:
        return 'Medium';
    }
  };

  const getDisplaySizeDescription = (sizeOption: 'extra small' | 'small' | 'medium' | 'large') => {
    switch (sizeOption) {
      case 'extra small':
        return 'Ultra-compact layout with minimal text';
      case 'small':
        return 'Compact layout with smaller text';
      case 'large':
        return 'Comfortable layout with larger text';
      default:
        return 'Standard layout and text size';
    }
  };

  const getDisplaySizeFontSize = (sizeOption: 'extra small' | 'small' | 'medium' | 'large') => {
    switch (sizeOption) {
      case 'extra small':
        return '0.75rem';
      case 'small':
        return '0.875rem';
      case 'medium':
        return '1rem';
      case 'large':
        return '1.25rem';
      default:
        return '1rem';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: theme.shape.borderRadius,
          minHeight: '60vh',
          bgcolor: theme.palette.background.paper,
          boxShadow: theme.shadows[24],
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}>
        <Typography variant="h6" component="div" sx={{ color: theme.palette.text.primary }}>
          Settings
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ 
            color: theme.palette.text.secondary,
            '&:hover': {
              bgcolor: theme.palette.action.hover,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: theme.palette.divider, px: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          aria-label="settings tabs"
          sx={{
            '& .MuiTab-root': {
              minHeight: 48,
              fontSize: '0.875rem',
              textTransform: 'none',
              fontWeight: 500,
              color: theme.palette.text.secondary,
              '&.Mui-selected': {
                color: theme.palette.primary.main,
                fontWeight: 600,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.primary.main,
            },
          }}
        >
          <Tab label="Profile" />
          <Tab label="Display" />
          <Tab label="API Keys" />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 4, bgcolor: theme.palette.background.default }}>
        {/* Profile Tab */}
        {activeTab === 0 && (
          <Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Username"
                value={profileData.username}
                onChange={(e) => handleProfileChange('username', e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="Email"
                value={profileData.email}
                onChange={(e) => handleProfileChange('email', e.target.value)}
                fullWidth
                size="small"
                type="email"
              />
              <TextField
                label="Client ID"
                value={profileData.clientId}
                onChange={(e) => handleProfileChange('clientId', e.target.value)}
                fullWidth
                size="small"
                disabled
              />
              <TextField
                label="Subdomain"
                value={profileData.subdomain}
                onChange={(e) => handleProfileChange('subdomain', e.target.value)}
                fullWidth
                size="small"
                disabled
              />
            </Box>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="contained" 
                onClick={handleSaveProfile}
                size="small"
              >
                Save Profile
              </Button>
            </Box>
          </Box>
        )}

        {/* Display Settings Tab */}
        {activeTab === 1 && (
          <Stack spacing={4}>
            {/* Theme Mode */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, color: theme.palette.text.primary, fontWeight: 500 }}>
                Theme Mode
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  icon={<LightIcon />}
                  label="Light"
                  variant={mode === 'light' ? 'filled' : 'outlined'}
                  color={mode === 'light' ? 'primary' : 'default'}
                  onClick={() => mode !== 'light' && toggleColorMode()}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  icon={<DarkIcon />}
                  label="Dark"
                  variant={mode === 'dark' ? 'filled' : 'outlined'}
                  color={mode === 'dark' ? 'primary' : 'default'}
                  onClick={() => mode !== 'dark' && toggleColorMode()}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
            </Box>

            {/* Display Size */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, color: theme.palette.text.primary, fontWeight: 500 }}>
                Display Size
              </Typography>
              <RadioGroup
                value={size}
                onChange={(e) => setDisplaySize(e.target.value as 'extra small' | 'small' | 'medium' | 'large')}
              >
                {(['extra small', 'small', 'medium', 'large'] as const).map((sizeOption) => (
                  <FormControlLabel
                    key={sizeOption}
                    value={sizeOption}
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getDisplaySizeIcon(sizeOption)}
                        <Box>
                          <Typography 
                            sx={{ 
                              color: theme.palette.text.primary,
                              fontSize: getDisplaySizeFontSize(sizeOption),
                              fontWeight: 500,
                              lineHeight: 1.2
                            }}
                          >
                            {getDisplaySizeLabel(sizeOption)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            {getDisplaySizeDescription(sizeOption)}
                          </Typography>
                        </Box>
                      </Box>
                    }
                    sx={{
                      alignItems: 'flex-start',
                      '& .MuiFormControlLabel-label': {
                        flex: 1,
                      }
                    }}
                  />
                ))}
              </RadioGroup>
            </Box>
          </Stack>
        )}

        {/* API Keys Tab */}
        {activeTab === 2 && (
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <KeyIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
              <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>API Key Management</Typography>
            </Box>

            {/* Error/Success Messages */}
            {apiKeyError && (
              <Alert severity="error" onClose={() => setApiKeyError(null)}>
                {apiKeyError}
              </Alert>
            )}
            {apiKeySuccess && (
              <Alert severity="success" onClose={() => setApiKeySuccess(null)}>
                {apiKeySuccess}
              </Alert>
            )}

            {/* API Key Display */}
            {isLoadingApiKey ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : apiKeyData ? (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Current API Key
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  p: 2,
                  border: 1,
                  borderColor: theme.palette.divider,
                  borderRadius: theme.shape.borderRadius,
                  backgroundColor: theme.palette.background.paper
                }}>
                  <TextField
                    value={showApiKey ? apiKeyData.apiKey : '••••••••••••••••••••••••••••••••'}
                    fullWidth
                    size="small"
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Tooltip title="Copy API Key">
                            <IconButton
                              onClick={copyApiKey}
                              size="small"
                              color={copySuccess ? "success" : "default"}
                            >
                              <CopyIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={showApiKey ? "Hide API Key" : "Show API Key"}>
                            <IconButton
                              onClick={() => setShowApiKey(!showApiKey)}
                              size="small"
                            >
                              {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )
                    }}
                  />
                </Box>
                
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Created: {new Date(apiKeyData.createdDate).toLocaleDateString()}
                  </Typography>
                  {apiKeyData.lastUsedDate && (
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      • Last used: {new Date(apiKeyData.lastUsedDate).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={generateNewApiKey}
                    disabled={isGeneratingKey}
                  >
                    {isGeneratingKey ? 'Generating...' : 'Generate New Key'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={revokeApiKey}
                    disabled={isRevokingKey}
                  >
                    {isRevokingKey ? 'Revoking...' : 'Revoke Key'}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                  No API key found for this client
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={generateNewApiKey}
                  disabled={isGeneratingKey}
                >
                  {isGeneratingKey ? 'Generating...' : 'Generate API Key'}
                </Button>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
            '&:hover': {
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsModal; 